import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AnalyticEvents } from '@constants/index';
import { datadogRum } from '@datadog/browser-rum';
import { useSnapshot } from 'valtio';
import { SarStore, setPaintedDoorText } from '@store/sar-store';
import { Button } from '@carvana/core-helios-button';
import { useRouter, ROUTES } from '../../providers/router';
import { Analytics } from '../../../../services/analytics-service';
import styles from './painted-door.module.css';

interface TextAreaProps {
  onHasValue: (hasValue: boolean) => void;
}

export interface TextAreaRef {
  getValue: () => string;
  focus: () => void;
}

const TextArea = React.forwardRef<TextAreaRef, TextAreaProps>(({ onHasValue }, ref) => {
  const [value, setValue] = useState('');
  const textAreaEl = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    focus: () => {
      if (textAreaEl.current) {
        textAreaEl.current.focus();
      }
    },
  }));

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    onHasValue(newValue.trim().length >= 5);
    setPaintedDoorText(newValue);
  };

  return <textarea ref={textAreaEl} rows={3} value={value} onChange={handleChange} />;
});

TextArea.propTypes = {
  onHasValue: PropTypes.func.isRequired,
};

export const PaintedDoor = () => {
  const { goToRoute } = useRouter();
  const { year, make, model, firstName, isTreatmentV1, isTreatmentV2, assignedBucket } = useSnapshot(
    SarStore.paintedDoor
  );
  const textAreaRef = useRef<TextAreaRef>(null);
  const [isButtonDisabled, setButtonDisabled] = useState(true);

  const handleNextRoute = () => {
    const issueDescription = textAreaRef.current?.getValue();
    Analytics.trackEvent(AnalyticEvents.paintedDoorClickedNext, {
      isTreatmentV1,
      isTreatmentV2,
      text: issueDescription,
      bucket: assignedBucket,
    });
    datadogRum.addAction(AnalyticEvents.paintedDoorClickedNext, {
      isTreatmentV1,
      isTreatmentV2,
      text: issueDescription,
      bucket: assignedBucket,
    });

    goToRoute(ROUTES.CATEGORIES);
  };

  useEffect(() => {
    Analytics.trackEvent(AnalyticEvents.paintedDoorViewed, { isTreatmentV1, isTreatmentV2, bucket: assignedBucket });
    datadogRum.addAction(AnalyticEvents.paintedDoorViewed);
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, []);

  return (
    <div className={styles['painted-door']}>
      <div className={styles['header']}>
        {isTreatmentV1 && `Tell us what's happening with your ${model}`}
        {isTreatmentV2 && `How can we help with your ${year} ${make} ${model}, ${firstName}?`}
      </div>

      <TextArea ref={textAreaRef} onHasValue={(hasValue) => setButtonDisabled(!hasValue)} />

      <Button onClick={handleNextRoute} disabled={isButtonDisabled}>
        Next
      </Button>
    </div>
  );
};
